/**
 * Runtime application configuration, derived from Vite-bundled `VITE_*` values.
 *
 * Every `VITE_*` value is public (bundled into the client). NEVER read secrets
 * here (spec §9.2). The portal never stores tokens; this module only exposes
 * non-sensitive deployment config.
 */

export interface AppEnv {
  /** API base, no trailing slash. e.g. "https://api.connect.realtime.zone/api" or "/api". */
  readonly apiBaseUrl: string;
  /** Public origin of this portal, used only to build absolute URLs when needed. */
  readonly portalOrigin: string;
  /** Enables non-PII internal diagnostics UI. */
  readonly internalDebug: boolean;
  /** Build version from CI metadata. */
  readonly appVersion: string;
  /** True only in the Playwright E2E build (in-browser mock backend). */
  readonly isE2E: boolean;
  /** Dev-only: serve the API from an in-browser MSW mock (no backend needed). */
  readonly useMocks: boolean;
}

function stripTrailingSlash(value: string): string {
  return value.endsWith('/') ? value.slice(0, -1) : value;
}

function parseBoolean(value: string | undefined, fallback: boolean): boolean {
  if (value === undefined) return fallback;
  return value === 'true' || value === '1';
}

function readEnv(source: ImportMetaEnv): AppEnv {
  const apiBaseUrl = stripTrailingSlash(source.VITE_API_BASE_URL?.trim() || '/api');
  const portalOrigin = stripTrailingSlash(
    source.VITE_PORTAL_ORIGIN?.trim() || window.location.origin,
  );
  return {
    apiBaseUrl,
    portalOrigin,
    internalDebug: parseBoolean(source.VITE_INTERNAL_DEBUG, false),
    appVersion: source.VITE_APP_VERSION?.trim() || 'dev',
    isE2E: parseBoolean(source.VITE_E2E, false),
    useMocks: parseBoolean(source.VITE_USE_MOCKS, false),
  };
}

export const env: AppEnv = readEnv(import.meta.env);
